/**
 * Centralized Date/Time Formatting Utility
 * 
 * All timestamps should flow through these utilities to ensure consistent
 * timezone handling across the application.
 * 
 * Policy:
 * - Backend stores and returns timestamps in UTC (ISO format with 'Z' suffix)
 * - Frontend converts to user's local browser timezone for display
 * - All formatting uses consistent style
 */

/**
 * Parse a timestamp from backend (handles various formats)
 * @param {string|Date|number} timestamp - The timestamp to parse
 * @returns {Date|null} - Parsed Date object or null if invalid
 */
export const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  try {
    // If already a Date object
    if (timestamp instanceof Date) {
      return isNaN(timestamp.getTime()) ? null : timestamp;
    }
    
    // If it's a number (Unix timestamp)
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    // If it's a string
    if (typeof timestamp === 'string') {
      // Handle ISO format (with or without Z suffix)
      let dateStr = timestamp;
      
      // If no timezone info, assume UTC
      if (!dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', 10)) {
        dateStr = dateStr + 'Z';
      }
      
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
  } catch (e) {
    console.warn('Failed to parse timestamp:', timestamp, e);
    return null;
  }
};

/**
 * Format a timestamp for display in the user's local timezone
 * @param {string|Date|number} timestamp - The timestamp to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeTime - Whether to include time (default: true)
 * @param {boolean} options.includeSeconds - Whether to include seconds (default: false)
 * @param {boolean} options.shortDate - Use shorter date format (default: false)
 * @param {string} options.fallback - Fallback text if timestamp is invalid (default: 'N/A')
 * @returns {string} - Formatted date/time string in local timezone
 */
export const formatTimestamp = (timestamp, options = {}) => {
  const {
    includeTime = true,
    includeSeconds = false,
    shortDate = false,
    fallback = 'N/A'
  } = options;
  
  const date = parseTimestamp(timestamp);
  if (!date) return fallback;
  
  try {
    const dateOptions = {
      year: 'numeric',
      month: shortDate ? 'numeric' : 'short',
      day: 'numeric',
    };
    
    if (includeTime) {
      dateOptions.hour = 'numeric';
      dateOptions.minute = '2-digit';
      if (includeSeconds) {
        dateOptions.second = '2-digit';
      }
      dateOptions.hour12 = true;
    }
    
    // Use the browser's local timezone
    return date.toLocaleString(undefined, dateOptions);
  } catch (e) {
    console.warn('Failed to format timestamp:', timestamp, e);
    return fallback;
  }
};

/**
 * Format a timestamp showing only the date (no time)
 * @param {string|Date|number} timestamp - The timestamp to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date string
 */
export const formatDate = (timestamp, options = {}) => {
  return formatTimestamp(timestamp, { ...options, includeTime: false });
};

/**
 * Format a timestamp showing date and time with seconds
 * @param {string|Date|number} timestamp - The timestamp to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date/time string with seconds
 */
export const formatDateTimeFull = (timestamp, options = {}) => {
  return formatTimestamp(timestamp, { ...options, includeTime: true, includeSeconds: true });
};

/**
 * Format a timestamp in short numeric format (e.g., 4/17/2026, 10:30 AM)
 * @param {string|Date|number} timestamp - The timestamp to format
 * @param {Object} options - Formatting options
 * @returns {string} - Formatted date/time string
 */
export const formatDateTimeShort = (timestamp, options = {}) => {
  return formatTimestamp(timestamp, { ...options, shortDate: true });
};

/**
 * Format a timestamp for display relative to now (e.g., "2 hours ago")
 * @param {string|Date|number} timestamp - The timestamp to format
 * @param {Object} options - Formatting options
 * @param {string} options.fallback - Fallback text if timestamp is invalid
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (timestamp, options = {}) => {
  const { fallback = 'N/A' } = options;
  
  const date = parseTimestamp(timestamp);
  if (!date) return fallback;
  
  try {
    const now = new Date();
    const diffMs = now - date;
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffSeconds < 60) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      // Fall back to full date for older timestamps
      return formatTimestamp(timestamp, { includeTime: false });
    }
  } catch (e) {
    console.warn('Failed to format relative time:', timestamp, e);
    return fallback;
  }
};

/**
 * Get current user's timezone name for display
 * @returns {string} - Timezone name (e.g., "America/New_York")
 */
export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return 'UTC';
  }
};

/**
 * Check if a timestamp is in the past
 * @param {string|Date|number} timestamp - The timestamp to check
 * @returns {boolean} - True if timestamp is in the past
 */
export const isPast = (timestamp) => {
  const date = parseTimestamp(timestamp);
  if (!date) return false;
  return date < new Date();
};

/**
 * Check if a timestamp is today (in local timezone)
 * @param {string|Date|number} timestamp - The timestamp to check
 * @returns {boolean} - True if timestamp is today
 */
export const isToday = (timestamp) => {
  const date = parseTimestamp(timestamp);
  if (!date) return false;
  
  const today = new Date();
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
};

export default {
  parseTimestamp,
  formatTimestamp,
  formatDate,
  formatDateTimeFull,
  formatDateTimeShort,
  formatRelativeTime,
  getUserTimezone,
  isPast,
  isToday,
};
